import { closeCurrentModal } from 'weblite-web-modal-viewer'
import { ChannelAdminChangeablePermissions } from '@weblite/ts-action/subscription'
import {
  addAdmin,
  changeAdminPermissions,
  decodeChannelAdminPermissions,
  decodeSubscriptionPermissions,
  getAdminPermissions,
  removeAdmins,
} from '$logic/subscription'
import {
  CHANNEL_ADMIN_RIGHTS,
  HallPermissionsPage,
} from '$common/hall-permissions-page'
import { t } from '$i18n'
import { useAsyncFunction } from '$func/async-function.react-hook'
import { closeChannelAdminPermissions } from '$logic/router'
import { showSaveChangesLoadingModal } from '$comp/modals/loading-modal'
import { useClientUserPermissions } from '$logic/subscription/react-hook'
import { showPromoteToOwnerConfirmModal } from '$comp/modals/confirmation-dialog'

interface Props {
  userId: string
  hallId: string
  mode: 'DEFINE' | 'EDIT'
}

export function ChannelAdminRights({ userId, hallId, mode }: Props) {
  const useAsyncFunctionCallBack =
    mode === 'DEFINE'
      ? () => decodeSubscriptionPermissions(hallId)
      : async () => {
          const adminPermission = await getAdminPermissions(hallId, userId)
          return decodeChannelAdminPermissions(hallId, adminPermission)
        }

  const { value: initialPermissions } = useAsyncFunction<
    ChannelAdminChangeablePermissions
  >(useAsyncFunctionCallBack, [hallId, userId])

  const clientUserPermissions = useClientUserPermissions(hallId)

  const onComplete = async (permissions: ChannelAdminChangeablePermissions) => {
    showSaveChangesLoadingModal()
    if (mode === 'DEFINE') {
      analite('CHAT_ADD_ADMIN', { type: 'CHANNEL' })
      await addAdmin(hallId, userId, permissions)
    } else await changeAdminPermissions(hallId, userId, permissions)
    await closeCurrentModal()
    await closeChannelAdminPermissions()
  }

  const onRemoveAdmin = async () => {
    showSaveChangesLoadingModal()
    await removeAdmins(hallId, [userId])
    await closeCurrentModal()
    await closeChannelAdminPermissions()
  }

  const onPromoteToOwner = async () =>
    showPromoteToOwnerConfirmModal(hallId, userId)

  return (
    <HallPermissionsPage
      confirmDisabled={false}
      rights={CHANNEL_ADMIN_RIGHTS.map(right => ({
        ...right,
        isLocked: !clientUserPermissions?.[right.id],
      }))}
      actionButtonProps={
        mode !== 'DEFINE'
          ? {
              text: t(
                'hallPermissionsPage.channelAdminRights.actionButtonText',
              ),
              color: 'error',
            }
          : undefined
      }
      secondaryActionButtonProps={{
        text: t('hallPermissionsPage.channelAdminRights.secondaryButtonText'),
        color: 'secondary',
      }}
      pageTitle={t('hallPermissionsPage.channelAdminRights.title')}
      subTitle={t('hallPermissionsPage.channelAdminRights.subTitle')}
      userId={userId}
      initialPermissions={initialPermissions}
      onBack={closeChannelAdminPermissions}
      onComplete={onComplete}
      onActionButtonClick={onRemoveAdmin}
      onSecondaryActionButton={
        clientUserPermissions?.isOwner ? onPromoteToOwner : undefined
      }
    />
  )
}
